import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Send, X, AlertCircle, Wrench, Truck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Dispatch modal for DI Tank Exchange requests — picks contractors and pops
// an Outlook BCC mailto:// pre-filled with the DI-specific scope of work.
// Mirrors SourceFromSuppliersModal but contractors-only.
export default function SourceFromContractorsModal({ requestId, token, onClose, onBroadcast }) {
  const [loading, setLoading] = useState(true);
  const [broadcasting, setBroadcasting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const authHeader = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/admin/di-tank-exchange/requests/${requestId}/source-preview`, { headers: authHeader })
      .then((r) => {
        if (!alive) return;
        setPreview(r.data);
        setSubject(r.data.draft_subject || "");
        setBody(r.data.draft_body || "");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load contractors"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [requestId, authHeader]);

  const visibleContractors = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (preview?.contractors || []).filter((c) => {
      if (!q) return true;
      const hay = `${c.name || ""} ${c.email || ""} ${c.contact_name || ""} ${(c.categories || []).join(" ")} ${(c.service_areas || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [preview, search]);

  const toggleOne = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelected(new Set(visibleContractors.map((c) => c.id)));
  const clearAll = () => setSelected(new Set());

  const handleBroadcast = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one contractor");
      return;
    }
    setBroadcasting(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/di-tank-exchange/requests/${requestId}/source-broadcast`,
        { contractor_ids: Array.from(selected), subject, body },
        { headers: authHeader },
      );
      const mailto = r.data?.mailto;
      const count = r.data?.recipient_count || 0;
      if (mailto) {
        window.location.href = mailto;
        toast.success(`Outlook draft opened — ${count} contractor${count === 1 ? "" : "s"} BCC'd. Click Send when ready.`);
        onBroadcast?.(r.data);
        setTimeout(() => onClose?.(), 800);
      } else {
        toast.error("Broadcast succeeded but no mailto link returned");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to dispatch");
    } finally {
      setBroadcasting(false);
    }
  };

  const req = preview?.request;
  const urgency = req?.urgency;
  const accent = urgency === "same_day_emergency" ? "rose" : "cyan";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="di-source-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent === "rose" ? "bg-rose-100 text-rose-700" : "bg-cyan-100 text-cyan-700"}`}>
              <Wrench className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-900">Source from Contractors</h3>
              <p className="text-xs text-slate-500">
                Dispatch DI tank exchange visit · {req?.company_name || "—"} · {req?.number_of_tanks || "?"} tank(s) · {preview?.urgency_label}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="di-source-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-cyan-600 mr-2" /> Loading contractors…</div>
        ) : (
          <div className="grid lg:grid-cols-[420px_1fr] flex-1 min-h-0">
            {/* LEFT: contractor picker */}
            <div className="border-r border-slate-200 max-h-[70vh] overflow-y-auto">
              <div className="p-4 sticky top-0 bg-white border-b border-slate-200 z-10 space-y-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search contractors by name, email, category, service area…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    data-testid="di-source-search"
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500"><strong>{selected.size}</strong> selected / {visibleContractors.length} visible</span>
                  <div className="flex gap-1.5">
                    <button onClick={selectAllVisible} className="text-cyan-700 hover:underline font-semibold" data-testid="di-source-select-all">Select visible</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={clearAll} className="text-slate-500 hover:underline" data-testid="di-source-clear">Clear</button>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {visibleContractors.map((c) => {
                  const isSelected = selected.has(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleOne(c.id)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-start gap-3 transition-colors ${isSelected ? "bg-cyan-50/60" : ""}`}
                      data-testid={`di-source-contractor-${c.id}`}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => {}} className="mt-1 accent-cyan-600 pointer-events-none" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{c.email}</p>
                        {(c.categories?.length > 0 || c.service_areas?.length > 0) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(c.categories || []).slice(0, 3).map((cat) => (
                              <span key={cat} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{cat}</span>
                            ))}
                            {(c.service_areas || []).slice(0, 2).map((area) => (
                              <span key={area} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700">{area}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
                {visibleContractors.length === 0 && (
                  <div className="text-center py-8 text-sm text-slate-400">No contractors match your search.</div>
                )}
              </div>
            </div>

            {/* RIGHT: email draft */}
            <div className="flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Subject</label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm" data-testid="di-source-subject" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Email body — admin can edit before sending</label>
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={18}
                    className="text-xs font-mono leading-relaxed resize-none"
                    data-testid="di-source-body"
                  />
                </div>
                <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    All <strong>{selected.size}</strong> selected contractor{selected.size === 1 ? "" : "s"} will be placed on <strong>BCC</strong>. Dispatching auto-advances this request from <em>New</em> to <em>Confirmed</em>.
                  </span>
                </div>
              </div>
              <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={onClose} disabled={broadcasting} data-testid="di-source-cancel">Cancel</Button>
                <Button
                  onClick={handleBroadcast}
                  disabled={broadcasting || selected.size === 0}
                  className={`${accent === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-cyan-600 hover:bg-cyan-700"} gap-2`}
                  data-testid="di-source-broadcast"
                >
                  {broadcasting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Opening Outlook…</>
                  ) : (
                    <><Send className="w-4 h-4" /> Dispatch to {selected.size} contractor{selected.size === 1 ? "" : "s"}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
